import { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { useWorkspaceStore } from "@/frontend/store/WorkspaceStore";
import { I18n } from "@/i18n/I18n";
import { Project } from "@/backend/models/project";
import { Select } from "../Select";

/**
 * Starts a new project from one of `templateProjects`. With a single template
 * (a template's own page) the choice is fixed; with several (the Library) the
 * user picks one, and the project name follows the template until they type.
 */
export function CreateProjectFromTemplateModal({ templateProjects, onClose }: { templateProjects: Project[]; onClose: () => void }) {
  const workspaces = useWorkspaceStore(state => state.workspaces);
  const createProjectFromTemplate = useProjectStore(state => state.createProjectFromTemplate);
  const [selectedTemplateId, setSelectedTemplateId] = useState(templateProjects.length === 1 ? templateProjects[0].id : '');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [title, setTitle] = useState(templateProjects.length === 1 ? templateProjects[0].title : '');
  const [titleEdited, setTitleEdited] = useState(false);
  const [templateError, setTemplateError] = useState('');
  const [workspaceError, setWorkspaceError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedTemplate = templateProjects.find(t => t.id === selectedTemplateId);

  const handleTemplateChange = (template: Project) => {
    setSelectedTemplateId(template.id);
    setTemplateError('');
    if (!titleEdited) {
      setTitle(template.title);
      setTitleError('');
    }
  };

  const handleWorkspaceChange = (value: string) => {
    setSelectedWorkspaceId(value);
    setWorkspaceError('');
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    setTitleEdited(true);
    setTitleError('');
  };

  const handleSubmit = async () => {
    let hasError = false;

    if (!selectedTemplateId) {
      setTemplateError(I18n.get('requiredFieldError', { field: I18n.get('template') }));
      hasError = true;
    }

    if (!selectedWorkspaceId) {
      setWorkspaceError(I18n.get('requiredFieldError', { field: I18n.get('workspace') }));
      hasError = true;
    }

    if (!title.trim()) {
      setTitleError(I18n.get('requiredFieldError', { field: I18n.get('projectName') }));
      hasError = true;
    }

    if (hasError) return;

    setIsSubmitting(true);
    await createProjectFromTemplate({ templateProjectId: selectedTemplateId, workspaceId: selectedWorkspaceId, title: title.trim() });
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Modal show onHide={onClose}>
      <Modal.Body>
        <Modal.Title className="pb-3">{I18n.get('createProjectFromTemplate')}</Modal.Title>
        <Form>
          {templateProjects.length > 1 && (
            <Form.Group className="mb-3">
              <Form.Label className="form-label-neutral">{I18n.get('template')}</Form.Label>
              <Select
                value={selectedTemplate?.title || I18n.get('selectTemplate')}
                label={I18n.get('selectTemplate')}
                neutral
              >
                {templateProjects.filter(t => t.id !== selectedTemplateId).map((template) => (
                  <button type="button" key={template.id} className="w-100 dropdown-item" onClick={() => handleTemplateChange(template)}>{template.title}</button>
                ))}
              </Select>
              {templateError && <div className="text-danger small mt-1">{templateError}</div>}
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label className="form-label-neutral">{I18n.get('projectName')}</Form.Label>
            <Form.Control
              type="text"
              className="form-control-neutral"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              isInvalid={!!titleError}
              aria-label={I18n.get('projectName')}
            />
            <Form.Control.Feedback type="invalid">
              {titleError}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label className="form-label-neutral">{I18n.get('workspace')}</Form.Label>
            <Select
              value={workspaces.find(w => w.id === selectedWorkspaceId)?.title || I18n.get('selectWorkspace')}
              label={I18n.get('selectWorkspace')}
              neutral
            >
              {workspaces.filter(w => w.id !== selectedWorkspaceId).map((workspace) => (
                <button type="button" key={workspace.id} className="w-100 dropdown-item" onClick={() => handleWorkspaceChange(workspace.id)}>{workspace.title}</button>
              ))}
            </Select>
            {workspaceError && <div className="text-danger small mt-1">{workspaceError}</div>}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className='fusa-btn-medium btn-blue-light' onClick={onClose} disabled={isSubmitting}>
          {I18n.get('cancel')}
        </Button>
        <Button className='fusa-btn-medium fusa-btn-primary' onClick={() => void handleSubmit()} disabled={isSubmitting}>
          {isSubmitting ? I18n.get('saving') : I18n.get('create')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
